import { Injectable } from '@angular/core';
import * as XLSX from 'xlsx';
import * as moment from 'moment';
import {
  Receipt,
  ReceiptAuthorityCommunicationState,
  ReceiptStatus,
  ReceiptType,
  ReceiptFiscalOperation,
  ReceiptSaleDocumentState,
} from './receipts.service';
import { tr } from '@util/tr';
import { formatReceiptUtcForDisplay } from './receipt-datetime.util';

export interface ExportReceiptsOptions {
  wineryChannels: { id: string; name?: string; channel?: { nameIt?: string; nameEn?: string } }[];
  pointOfSales: { id: string; name: string }[];
  startDate?: Date;
  endDate?: Date;
  appliedFilters?: Record<string, any>;
}

@Injectable({ providedIn: 'root' })
export class ExportReceiptsService {

  exportToXLSX(receipts: Receipt[], opts: ExportReceiptsOptions): void {
    const filename = this.buildFilename('xlsx', opts.startDate, opts.endDate);
    const allRows = this.expandRows(receipts, opts);

    const wb = XLSX.utils.book_new();

    this.addDocumentsSheet(wb, allRows);
    this.addFiltersSheet(wb, allRows.length, opts);

    const buffer = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  exportToCSV(receipts: Receipt[], opts: ExportReceiptsOptions): void {
    const filename = this.buildFilename('csv', opts.startDate, opts.endDate);
    const allRows = this.expandRows(receipts, opts);
    const colDefs = this.buildColumns();
    const headers = colDefs.map((c) => c.header as string);

    const escape = (v: any) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const csvContent =
      '\uFEFF' + // BOM for Excel UTF-8
      [
        headers.map(escape).join(','),
        ...allRows.map((row) => row.map(escape).join(',')),
      ].join('\r\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  // ─── Filename ─────────────────────────────────────────────────────────────

  private buildFilename(ext: string, startDate?: Date, endDate?: Date): string {
    const ts = moment().format('YYYYMMDDHHmmss');
    if (startDate && endDate) {
      const s = moment(startDate).format('DDMMYYYY');
      const e = moment(endDate).format('DDMMYYYY');
      return `DocumentiCommerciali_${s}_${e}_${ts}.${ext}`;
    }
    return `DocumentiCommerciali_Completo_${ts}.${ext}`;
  }

  // ─── Row expansion (original + storno rows) ───────────────────────────────

  private expandRows(receipts: Receipt[], opts: ExportReceiptsOptions): any[][] {
    const rows: any[][] = [];
    for (const r of receipts) {
      rows.push(this.buildRowData(r, opts, false));
      // Legacy: synthetic negative row for old in-place storno/refund model only.
      const isFiscalReversalDoc =
        r.fiscalOperation === ReceiptFiscalOperation.CORRECTION ||
        r.fiscalOperation === ReceiptFiscalOperation.CANCELLATION;
      const isLegacyStorno =
        r.authorityCommunicationState === ReceiptAuthorityCommunicationState.STORNED ||
        r.status === ReceiptStatus.CANCELLED;
      if (!isFiscalReversalDoc && isLegacyStorno) {
        rows.push(this.buildRowData(r, opts, true));
      }
    }
    return rows;
  }

  // ─── Sheets ───────────────────────────────────────────────────────────────

  private addDocumentsSheet(wb: XLSX.WorkBook, allRows: any[][]): void {
    const colDefs = this.buildColumns();
    const headers = colDefs.map((c) => c.header as string);

    const dataStart = 2;
    const dataEnd = allRows.length + 1;
    const sumKeys = new Set([
      'totalAmount',
      'productSubtotal',
      'net22',
      'vat22',
      'net10',
      'vat10',
      'net4',
      'vat4',
      'totalVat',
    ]);
    const totalRow: any[] = [];
    for (let c = 0; c < colDefs.length; c++) {
      const key = colDefs[c].key;
      if (c === 0) {
        totalRow.push(tr('receipts.export.total'));
      } else if (sumKeys.has(key)) {
        const colL = this.excelColLetter(c);
        totalRow.push({ f: `SUM(${colL}${dataStart}:${colL}${dataEnd})` });
      } else {
        totalRow.push('');
      }
    }

    const wsData = [headers, ...allRows, totalRow];
    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // Set column widths
    ws['!cols'] = colDefs.map((c) => ({ wch: Math.floor((c.width || 12) * 0.8) }));

    // Enable autofilter
    ws['!autofilter'] = { ref: XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: 0, c: colDefs.length - 1 } }) };

    XLSX.utils.book_append_sheet(wb, ws, tr('receipts.export.sheet.documents'));
  }

  private addFiltersSheet(wb: XLSX.WorkBook, docCount: number, opts: ExportReceiptsOptions): void {
    const rows: any[][] = [];

    rows.push([tr('receipts.export.meta.exportDateTime'), moment().format('DD/MM/YYYY HH:mm:ss')]);
    rows.push([tr('receipts.export.meta.documentsExported'), String(docCount)]);
    if (opts.startDate) rows.push([tr('receipts.export.meta.dateFrom'), moment(opts.startDate).format('DD/MM/YYYY')]);
    if (opts.endDate)   rows.push([tr('receipts.export.meta.dateTo'),   moment(opts.endDate).format('DD/MM/YYYY')]);

    if (opts.appliedFilters) {
      const skip = new Set(['page', 'count', 'sortBy', 'wineryId', 'startDate', 'endDate']);
      Object.entries(opts.appliedFilters).forEach(([key, value]) => {
        if (skip.has(key) || value == null || value === '') return;
        rows.push([key, String(value)]);
      });
    }

    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws['!cols'] = [{ wch: 30 }, { wch: 50 }];
    XLSX.utils.book_append_sheet(wb, ws, tr('receipts.export.sheet.filters'));
  }

  // ─── Column schema ────────────────────────────────────────────────────────

  private buildColumns(): { header: string; key: string; width: number }[] {
    return [
      { header: tr('receipts.export.col.documentNo'),            key: 'progressiveNumber',    width: 16 },
      { header: tr('receipts.fiscalOperation.type'),             key: 'fiscalTipo',           width: 14 },
      { header: tr('receipts.export.col.adeDocumentNo'),         key: 'adeDocumentNumber',    width: 20 },
      { header: tr('receipts.export.col.issuanceDate'),          key: 'issuanceDate',         width: 16 },
      { header: tr('receipts.export.col.issuanceTime'),          key: 'issuanceTime',         width: 14 },
      { header: tr('receipts.export.col.customer'),              key: 'customer',             width: 28 },
      { header: tr('receipts.export.col.salesChannel'),          key: 'salesChannel',         width: 22 },
      { header: tr('receipts.export.col.checkoutPoint'),         key: 'checkoutPoint',        width: 28 },
      { header: tr('receipts.export.col.posTerminal'),           key: 'posTerminal',          width: 28 },
      { header: tr('receipts.export.col.totalAmount'),           key: 'totalAmount',          width: 18 },
      { header: tr('receipts.export.col.adeFeeStatus'),          key: 'adeStatus',            width: 18 },
      { header: tr('receipts.export.col.documentStatus'),        key: 'documentStatus',       width: 18 },
      { header: tr('receipts.export.col.tssSerial'),             key: 'tssSerial',            width: 28 },
      { header: tr('receipts.export.col.fiskalyTransactionId'),  key: 'fiskalyTransactionId', width: 38 },
      { header: tr('receipts.export.col.stripePaymentId'),       key: 'stripePaymentId',      width: 32 },
      { header: tr('receipts.export.col.customerEmail'),         key: 'customerEmail',        width: 30 },
      { header: tr('receipts.export.col.productSubtotal'),       key: 'productSubtotal',      width: 20 },
      { header: tr('receipts.export.col.taxable22'),             key: 'net22',                width: 22 },
      { header: tr('receipts.export.col.vat22'),                 key: 'vat22',                width: 16 },
      { header: tr('receipts.export.col.taxable10'),             key: 'net10',                width: 22 },
      { header: tr('receipts.export.col.vat10'),                 key: 'vat10',                width: 16 },
      { header: tr('receipts.export.col.taxable4'),              key: 'net4',                 width: 22 },
      { header: tr('receipts.export.col.vat4'),                  key: 'vat4',                 width: 16 },
      { header: tr('receipts.export.col.totalVat'),              key: 'totalVat',             width: 16 },
      { header: tr('receipts.export.col.cancellationDate'),      key: 'cancellationDate',     width: 20 },
      { header: tr('receipts.export.col.refundDate'),            key: 'refundDate',           width: 20 },
      { header: tr('receipts.export.col.generatedBy'),           key: 'generatedBy',          width: 24 },
      { header: tr('receipts.export.col.notes'),                 key: 'notes',                width: 30 },
    ];
  }

  // ─── Row data builder ─────────────────────────────────────────────────────

  private buildRowData(r: Receipt, opts: ExportReceiptsOptions, isStornoRow: boolean): any[] {
    const rd = this.parseReceiptData(r);
    const ed = this.parseExtraData(r);
    const sign = isStornoRow ? -1 : this.exportAmountSign(r);

    const vatBreakdown = this.computeVatBreakdown(r, rd);
    const posName = this.getPointOfSaleName(r, opts.pointOfSales);
    const posId = r.pointOfSaleWineSuiteId ? String(r.pointOfSaleWineSuiteId) : '';
    const checkoutPoint = posId ? `${posName} - ${posId}` : posName;
    const terminalName = this.getTerminalName(rd);
    const terminalId = this.getTerminalId(rd);
    const posTerminal = terminalId ? `${terminalName} - ${terminalId}` : terminalName || '';

    const issuanceDate = isStornoRow ? this.getStornoDate(r, ed) : formatReceiptUtcForDisplay(r.createdAt, 'DD/MM/YYYY');
    const issuanceTime = isStornoRow ? this.getStornoTime(r, ed) : formatReceiptUtcForDisplay(r.createdAt, 'HH:mm:ss');

    return [
      this.getDocNumber(r, isStornoRow),
      this.getFiscalTipoLabel(r),
      r.adeDocumentNumber ?? '',
      issuanceDate,
      issuanceTime,
      this.getCustomerName(r, rd),
      this.getChannelName(r, rd, opts.wineryChannels),
      checkoutPoint,
      posTerminal,
      this.getTotalValue(r) * sign,
      this.getAdeStatusLabel(r),
      isStornoRow ? this.getStornoDocumentStatusLabel(r) : this.getDocumentStatusLabel(r),
      this.getTssSerial(rd, ed),
      isStornoRow ? this.getStornoFiskalyTransactionId(ed) : this.getFiskalyTransactionId(ed),
      isStornoRow ? this.getStornoStripePaymentId(rd) : this.getStripePaymentId(rd),
      this.getCustomerEmail(rd),
      this.getProductSubtotal(r) * sign,
      vatBreakdown.net22 * sign,
      vatBreakdown.vat22 * sign,
      vatBreakdown.net10 * sign,
      vatBreakdown.vat10 * sign,
      vatBreakdown.net4  * sign,
      vatBreakdown.vat4  * sign,
      this.getTotalVat(r) * sign,
      isStornoRow ? issuanceDate : this.getCancellationDate(r, ed),
      isStornoRow ? issuanceDate : this.getRefundDate(r, ed),
      this.getGeneratedBy(rd, ed),
      isStornoRow ? this.buildStornoNotes(r) : this.getNotes(rd),
    ];
  }

  // ─── Storno helpers ───────────────────────────────────────────────────────

  /** Negate amounts for CORRECTION/CANCELLATION documents (aligned with Fiskaly eReceipt polarity). */
  private exportAmountSign(r: Receipt): number {
    const op = r.fiscalOperation ?? ReceiptFiscalOperation.RECEIPT;
    if (op === ReceiptFiscalOperation.CORRECTION || op === ReceiptFiscalOperation.CANCELLATION) {
      return -1;
    }
    return 1;
  }

  private getDocNumber(r: Receipt, isStornoRow: boolean): string {
    const base =
      r.internalDocumentNumber ||
      (r.progressiveNumber != null ? `#${r.progressiveNumber}` : (r.externalReceiptId || '-'));
    if (!isStornoRow) return base;
    const suffix = r.authorityCommunicationState === ReceiptAuthorityCommunicationState.STORNED
      ? '-ANN' : '-RES';
    return `${base}${suffix}`;
  }

  private getFiscalTipoLabel(r: Receipt): string {
    const op = r.fiscalOperation ?? ReceiptFiscalOperation.RECEIPT;
    const labels: Record<ReceiptFiscalOperation, string> = {
      [ReceiptFiscalOperation.RECEIPT]: tr('receipts.fiscalOperation.sale'),
      [ReceiptFiscalOperation.CORRECTION]: tr('receipts.fiscalOperation.return'),
      [ReceiptFiscalOperation.CANCELLATION]: tr('receipts.fiscalOperation.cancellation'),
    };
    return labels[op] ?? String(op);
  }

  /** 0-based column index → Excel column letters (A, B, …, Z, AA, …). */
  private excelColLetter(zeroBasedIndex: number): string {
    let n = zeroBasedIndex + 1;
    let s = '';
    while (n > 0) {
      const rem = (n - 1) % 26;
      s = String.fromCharCode(65 + rem) + s;
      n = Math.floor((n - 1) / 26);
    }
    return s;
  }

  private getStornoDate(r: Receipt, ed: any): string {
    const d = ed?.metadata?.cancellationDate ?? ed?.cancellationDate ?? r.updatedAt;
    return formatReceiptUtcForDisplay(d ?? r.updatedAt, 'DD/MM/YYYY');
  }

  private getStornoTime(r: Receipt, ed: any): string {
    const d = ed?.metadata?.cancellationDate ?? ed?.cancellationDate ?? r.updatedAt;
    return formatReceiptUtcForDisplay(d ?? r.updatedAt, 'HH:mm:ss');
  }

  private getStornoDocumentStatusLabel(r: Receipt): string {
    return r.authorityCommunicationState === ReceiptAuthorityCommunicationState.STORNED
      ? tr('Cancelled') : tr('Refunded');
  }

  private getStornoFiskalyTransactionId(ed: any): string {
    return ed?.metadata?.stornoTransactionId ?? ed?.stornoTransactionId ?? '';
  }

  private getStornoStripePaymentId(rd: any): string {
    const base = this.getStripePaymentId(rd);
    return base ? `${base}_refund` : '';
  }

  private buildStornoNotes(r: Receipt): string {
    const base = r.progressiveNumber ? `#${r.progressiveNumber}` : (r.externalReceiptId || '');
    return r.authorityCommunicationState === ReceiptAuthorityCommunicationState.STORNED
      ? `${tr('receipts.export.cancellationDocumentFor')} ${base}`
      : `${tr('receipts.export.refundDocumentFor')} ${base}`;
  }

  // ─── Data extractors ──────────────────────────────────────────────────────

  private parseReceiptData(r: Receipt): any {
    try {
      if (!r.receiptData) return {};
      return typeof r.receiptData === 'string' ? JSON.parse(r.receiptData) : r.receiptData;
    } catch { return {}; }
  }

  private parseExtraData(r: Receipt): any {
    try {
      if (!r.extraData) return {};
      return typeof r.extraData === 'string' ? JSON.parse(r.extraData) : r.extraData;
    } catch { return {}; }
  }

  private getTotalValue(r: Receipt): number {
    if (r.totalCents != null) return (r.totalCents - (r.discountTotalCents ?? 0)) / 100;
    return 0;
  }

  private getProductSubtotal(r: Receipt): number {
    return r.totalCents != null ? r.totalCents / 100 : 0;
  }

  private getTotalVat(r: Receipt): number {
    return r.vatAmountCents != null ? r.vatAmountCents / 100 : 0;
  }

  private computeVatBreakdown(r: Receipt, rd: any): { net22: number; vat22: number; net10: number; vat10: number; net4: number; vat4: number } {
    const zero = { net22: 0, vat22: 0, net10: 0, vat10: 0, net4: 0, vat4: 0 };

    if (rd?.vat_summary && Array.isArray(rd.vat_summary)) {
      const result = { ...zero };
      for (const entry of rd.vat_summary) {
        const rawRate = parseFloat(entry.rate ?? entry.percentage ?? 0);
        const rate = rawRate > 100 ? rawRate / 100 : rawRate;
        const net  = parseFloat(entry.net ?? entry.exclusive ?? 0);
        const vat  = parseFloat(entry.vat ?? entry.tax ?? 0);
        if (Math.abs(rate - 22) < 0.1)      { result.net22 += net; result.vat22 += vat; }
        else if (Math.abs(rate - 10) < 0.1) { result.net10 += net; result.vat10 += vat; }
        else if (Math.abs(rate - 4) < 0.1)  { result.net4  += net; result.vat4  += vat; }
      }
      return result;
    }

    const lines: any[] = rd?.lines ?? rd?.items ?? [];
    if (lines.length > 0) {
      const result = { ...zero };
      for (const line of lines) {
        const rawVat = parseFloat(line.vat_percent ?? line.vatPercent ?? 22);
        const vatPct = rawVat > 100 ? rawVat / 100 : rawVat;
        const net    = parseFloat(line.net_total_cents ?? line.netTotalCents ?? 0) / 100;
        const vat    = parseFloat(line.vat_amount_cents ?? line.vatAmountCents ?? 0) / 100;
        if (Math.abs(vatPct - 22) < 0.1)      { result.net22 += net; result.vat22 += vat; }
        else if (Math.abs(vatPct - 10) < 0.1) { result.net10 += net; result.vat10 += vat; }
        else if (Math.abs(vatPct - 4) < 0.1)  { result.net4  += net; result.vat4  += vat; }
      }
      return result;
    }

    // Fallback: treat everything as 22%
    if (r.totalNoVatCents != null && r.vatAmountCents != null) {
      return { net22: r.totalNoVatCents / 100, vat22: r.vatAmountCents / 100, net10: 0, vat10: 0, net4: 0, vat4: 0 };
    }
    return zero;
  }

  private getCustomerName(r: Receipt, rd: any): string {
    if (rd?.customer?.name) return rd.customer.name;
    if (rd?.customerName)   return rd.customerName;
    if (rd?.customer_name)  return rd.customer_name;
    return tr('receipts.export.genericCustomer');
  }

  private getCustomerEmail(rd: any): string {
    return rd?.customer?.email ?? rd?.customerEmail ?? rd?.customer_email ?? '';
  }

  private getChannelName(r: Receipt, rd: any, wineryChannels: { id: string; name?: string; channel?: { nameIt?: string; nameEn?: string } }[]): string {
    if (r.receiptType === ReceiptType.RESERVATION) return tr('Reservation');
    if (r.receiptType === ReceiptType.GIFT) return tr('Gift');
    const channelId = r.wineryChannelId ?? rd?.wineryChannelId ?? rd?.channelId ?? rd?.channel?.id;
    if (!channelId) return '-';
    const ch = wineryChannels.find((c) => c.id === channelId);
    return ch?.name ?? ch?.channel?.nameIt ?? ch?.channel?.nameEn ?? channelId;
  }

  private getPointOfSaleName(r: Receipt, pointOfSales: { id: string; name: string }[]): string {
    if (!r.pointOfSaleWineSuiteId) return '-';
    const pos = pointOfSales.find((p) => p.id === String(r.pointOfSaleWineSuiteId));
    return pos?.name ?? String(r.pointOfSaleWineSuiteId);
  }

  private getTerminalName(rd: any): string {
    return rd?.terminal?.name ?? rd?.posTerminal?.name ?? rd?.terminalName ?? '';
  }

  private getTerminalId(rd: any): string {
    return rd?.terminal?.id ?? rd?.posTerminal?.id ?? rd?.terminalId ?? '';
  }

  private getAdeStatusLabel(r: Receipt): string {
    if (r.authorityCommunicationState) {
      const map: Record<string, string> = {
        [ReceiptAuthorityCommunicationState.TODO]:        tr('Created'),
        [ReceiptAuthorityCommunicationState.IN_PROGRESS]: tr('Waiting'),
        [ReceiptAuthorityCommunicationState.SUCCESS]:     tr('Completed'),
        [ReceiptAuthorityCommunicationState.FAILED]:      tr('Failed'),
        [ReceiptAuthorityCommunicationState.STORNED]:     tr('Cancelled'),
        [ReceiptAuthorityCommunicationState.PENDING]:     tr('Waiting'),
      };
      return map[r.authorityCommunicationState] ?? r.authorityCommunicationState;
    }
    if (r.status === ReceiptStatus.COMPLETED)  return tr('Completed');
    if (r.status === ReceiptStatus.PENDING)    return tr('Waiting');
    if (r.status === ReceiptStatus.PROCESSING) return tr('Waiting');
    if (r.status === ReceiptStatus.FAILED)     return tr('Failed');
    if (r.status === ReceiptStatus.CANCELLED)  return tr('Cancelled');
    return tr('Created');
  }

  private getDocumentStatusLabel(r: Receipt): string {
    if (r.saleDocumentState === ReceiptSaleDocumentState.FULL_RETURN) return tr('receipts.saleDocumentState.fullReturn');
    if (r.saleDocumentState === ReceiptSaleDocumentState.PARTIAL_RETURN) return tr('receipts.saleDocumentState.partialReturn');
    if (r.saleDocumentState === ReceiptSaleDocumentState.VOIDED) return tr('receipts.saleDocumentState.voided');
    if (r.status === ReceiptStatus.CORRECTION) return tr('Refunded');
    if (
      r.authorityCommunicationState === ReceiptAuthorityCommunicationState.STORNED ||
      r.status === ReceiptStatus.CANCELLED
    ) return tr('Cancelled');
    return tr('Generated');
  }

  private getTssSerial(rd: any, ed: any): string {
    return ed?.metadata?.tssSerial ?? ed?.metadata?.tss_serial ?? ed?.tssSerial
        ?? rd?.tss_serial ?? rd?.tssSerial ?? '';
  }

  private getFiskalyTransactionId(ed: any): string {
    return ed?.metadata?.transactionId ?? ed?.transactionId ?? '';
  }

  private getStripePaymentId(rd: any): string {
    if (rd?.payments && Array.isArray(rd.payments)) {
      const p = rd.payments.find((x: any) => x.provider === 'stripe' || x.provider === 'dwspay')
             ?? rd.payments.find((x: any) => x.external_id);
      if (p?.external_id) return p.external_id;
    }
    return '';
  }

  private getCancellationDate(r: Receipt, ed: any): string {
    if (
      r.authorityCommunicationState === ReceiptAuthorityCommunicationState.STORNED ||
      r.status === ReceiptStatus.CANCELLED
    ) {
      const d = ed?.metadata?.cancellationDate ?? ed?.cancellationDate ?? r.updatedAt;
      return d ? formatReceiptUtcForDisplay(d, 'DD/MM/YYYY') : '';
    }
    return '';
  }

  private getRefundDate(r: Receipt, ed: any): string {
    const d = ed?.metadata?.refundDate ?? ed?.refundDate;
    return d ? formatReceiptUtcForDisplay(d, 'DD/MM/YYYY') : '';
  }

  private getGeneratedBy(rd: any, ed: any): string {
    return ed?.metadata?.generatedBy ?? ed?.generatedBy ?? rd?.generatedBy ?? rd?.created_by ?? '';
  }

  private getNotes(rd: any): string {
    return rd?.notes ?? rd?.note ?? '';
  }
}
